import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import APILoadingStatus from 'types/api-loading-status'
import asyncReducers from '../../../utils/async-reducers'
import toast from 'react-hot-toast'
import {
  getProgramById,
  createProgram,
  getPrograms,
  getProgramScheduleById,
  getPublicPrograms,
  getProgramRegistrations,
  discardProgram,
  updateProgram,
  toggleProgramVisibility,
} from './programs.actions'
import { Program, SingleProgram, PublicProgram } from 'models/program.model'
import { AppointmentDetail } from 'services/appointment'
import { ProgramRegistrationsData } from 'services/programs/program.types'

// Helper to safely handle error messages that can be string or boolean
const getErrorMessage = (error: string | boolean): string =>
  typeof error === 'string' ? error : 'An error occurred'

// @TODO - Add Public Programs types

export type ProgramState = {
  programStatus: APILoadingStatus
  currentProgramStatus: APILoadingStatus
  registrationsStatus: APILoadingStatus
  programs: Program[] | null
  currentProgram: SingleProgram | null
  programSchedule: AppointmentDetail[] | null
  programRegistrations: ProgramRegistrationsData | null
  currentPage: number
  totalPrograms: number
  // product#615: staff-scope counts from the GET /programs `description`, used
  // for the hidden-from-players banner. Independent of the active ?visible
  // filter, so they stay accurate as staff flip All/Published/Hidden.
  seasonCount: number
  hiddenCount: number
  visibleCount: number
  publicPrograms: PublicProgram[] | null
}

const initialState: ProgramState = {
  programStatus: APILoadingStatus.Idle,
  currentProgramStatus: APILoadingStatus.Idle,
  registrationsStatus: APILoadingStatus.Idle,
  programs: null,
  currentProgram: null,
  programSchedule: null,
  programRegistrations: null,
  currentPage: 1,
  totalPrograms: 0,
  seasonCount: 0,
  hiddenCount: 0,
  visibleCount: 0,
  publicPrograms: null,
}

const programSlice = createSlice({
  name: 'program',
  initialState,
  reducers: {
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload
    },
    cleanCurrentProgram(state) {
      state.currentProgram = null
      state.programSchedule = null
    },
  },
  extraReducers: builder => {
    asyncReducers(builder, getPrograms, 'programStatus', (state, action) => {
      if (action.payload.error) {
        toast.error(getErrorMessage(action.payload.error))
        return
      }
      const description = action.payload.description
      if (!description) return
      state.totalPrograms = description.total_count
      // product#615 (be#212): drive the hidden-from-players banner from the
      // server scope counts (NOT computed client-side). Absent for non-staff.
      state.seasonCount = description.season_count ?? 0
      state.hiddenCount = description.hidden_count ?? 0
      state.visibleCount = description.visible_count ?? 0
      // be#212/product#615: the endpoint serializes `data` as a single OBJECT
      // (not an array) when exactly one program matches (programs_class.read
      // sets many=False at count==1). Normalize defensively so a 1-result
      // page/filter still renders as a list instead of throwing on .map.
      const data = action.payload.data
      state.programs = Array.isArray(data)
        ? data
        : [(data as unknown) as Program]
    })
    asyncReducers(
      builder,
      getProgramById,
      'currentProgramStatus',
      (state, action) => {
        if (action.payload.error) {
          toast.error(getErrorMessage(action.payload.error))
          return
        }
        state.currentProgram = action.payload.data
      }
    )
    asyncReducers(
      builder,
      getProgramRegistrations,
      'registrationsStatus',
      (state, action) => {
        if (action.payload.error) {
          toast.error(getErrorMessage(action.payload.error))
          return
        }
        state.programRegistrations = action.payload.data
      }
    )
    asyncReducers(
      builder,
      createProgram,
      'currentProgramStatus',
      (state, action) => {
        if (action.payload.error) {
          toast.error(getErrorMessage(action.payload.error))
          return
        }
        toast.success('Your program was created.')
      }
    )
    asyncReducers(
      builder,
      getProgramScheduleById,
      'currentProgramStatus',
      (state, action) => {
        if (action.payload.error) {
          toast.error(getErrorMessage(action.payload.error))
          return
        }
        state.programSchedule = action.payload.data
      }
    )
    asyncReducers(
      builder,
      getPublicPrograms,
      'publicPrograms',
      (state, action) => {
        if (action.payload.error) {
          toast.error(getErrorMessage(action.payload.error))
          return
        }
        state.publicPrograms = action.payload.data
      }
    )
    // product#586: toast success/error on program discard; the page re-fetches
    // the list on success so the discarded program drops out.
    asyncReducers(
      builder,
      discardProgram,
      'currentProgramStatus',
      (_state, action) => {
        const { error, description } = action.payload
        if (error) {
          // product#586: the already-deleted / not-found path returns a 200
          // error-envelope { error: 'not_found', ... } (be#202 uses
          // item_not_found_error, which is HTTP 200, not 404). Show a clear
          // "already removed" message rather than the raw code; the page
          // self-heals by refetching so the stale row drops out.
          toast.error(
            error === 'not_found'
              ? 'This program was already removed.'
              : getErrorMessage(description || error)
          )
          return
        }
        toast.success('Program deleted.')
      },
      (_state, action) => {
        // product#586: a non-manager's delete returns HTTP 403 and REJECTS;
        // without this handler it failed silently (the exact bug #586 fixes).
        // Also covers network/5xx. One toast per path: the service no longer
        // toasts, so there is no double with the fulfilled handler.
        const status = (action.payload as { status?: number } | undefined)
          ?.status
        toast.error(
          status === 403
            ? "You don't have permission to delete this program."
            : 'Could not delete program. Please try again.'
        )
      }
    )
    // product#169: toast success/error on metadata update (mirrors discard).
    // The page re-fetches on success so the edited program reflects the change.
    asyncReducers(
      builder,
      updateProgram,
      'currentProgramStatus',
      (_state, action) => {
        const { error, description } = action.payload
        if (error) {
          // not_found (200 envelope, be#205) = discarded/cross-facility.
          toast.error(
            error === 'not_found'
              ? 'This program was already removed.'
              : getErrorMessage(description || error)
          )
          return
        }
        toast.success('Program updated.')
      },
      (_state, action) => {
        const status = (action.payload as { status?: number } | undefined)
          ?.status
        toast.error(
          status === 403
            ? "You don't have permission to edit this program."
            : 'Could not update program. Please try again.'
        )
      }
    )
    // product#615: optimistic per-program visibility toggle. Flip the row in
    // place on `pending` (instant feedback + banner/filter recompute), and
    // revert on failure. Does NOT touch `programStatus`, so the list is not
    // blanked by a spinner while a single row toggles.
    builder.addCase(toggleProgramVisibility.pending, (state, action) => {
      const { programId, visible } = action.meta.arg
      if (state.programs) {
        state.programs = state.programs.map(program =>
          program.id === programId ? { ...program, visible } : program
        )
      }
    })
    builder.addCase(toggleProgramVisibility.fulfilled, (state, action) => {
      const payload = action.payload as
        | { error?: boolean | string }
        | undefined
      if (payload?.error) {
        // 200-envelope error (e.g. not_found): revert the optimistic flip.
        const { programId, visible } = action.meta.arg
        if (state.programs) {
          state.programs = state.programs.map(program =>
            program.id === programId
              ? { ...program, visible: !visible }
              : program
          )
        }
        toast.error(
          payload.error === 'not_found'
            ? 'This program was already removed.'
            : getErrorMessage(payload.error)
        )
        return
      }
      toast.success(
        action.meta.arg.visible
          ? 'Program is now visible to players.'
          : 'Program is now hidden from players.'
      )
    })
    builder.addCase(toggleProgramVisibility.rejected, (state, action) => {
      // 403 (no permission) / network / 5xx: revert the optimistic flip.
      const { programId, visible } = action.meta.arg
      if (state.programs) {
        state.programs = state.programs.map(program =>
          program.id === programId
            ? { ...program, visible: !visible }
            : program
        )
      }
      const status = (action.payload as { status?: number } | undefined)
        ?.status
      toast.error(
        status === 403
          ? "You don't have permission to change this program's visibility."
          : 'Could not update program visibility. Please try again.'
      )
    })
  },
})

export default programSlice.reducer
export const programActions = {
  ...programSlice.actions,
  ...programSlice.reducer,
  getPrograms,
  getProgramById,
  getProgramRegistrations,
  getProgramScheduleById,
  getPublicPrograms,
  createProgram,
  discardProgram,
  updateProgram,
  toggleProgramVisibility,
}
